<script setup>
import { ref, watch, onMounted, isVNode } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import {
  CollapsibleRoot,
  CollapsibleTrigger,
  CollapsibleContent,
} from 'reka-ui';
import {
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarMenuBadge,
} from './index';
import Icon from 'next/icon/Icon.vue';
import UNANavSubGroup from './UNANavSubGroup.vue';
import SidebarMenuSection from './SidebarMenuSection.vue';

const props = defineProps({
  items: { type: Array, required: true },
});

const route = useRoute();
const router = useRouter();

// Track expanded items
const expandedItems = ref(new Set());

const resolvePath = to => {
  if (typeof to === 'string') return to;
  try {
    return router.resolve(to).path;
  } catch (e) {
    return '';
  }
};

const isActive = item => {
  if (!item.to) return false;
  const itemPath = resolvePath(item.to);
  if (route.path === itemPath) return true;
  if (item.activeOn && item.activeOn.includes(route.name)) return true;
  return false;
};

const hasActiveChild = item => {
  if (!item.children) return false;

  // Fonction récursive pour vérifier tous les enfants et sous-enfants
  const checkChildren = children => {
    return children.some(child => {
      if (child.to) {
        const childPath = resolvePath(child.to);
        if (route.path === childPath) return true;
        if (child.activeOn && child.activeOn.includes(route.name)) return true;
      }
      // Si l'enfant a lui-même des enfants, vérifier récursivement
      if (child.children) {
        return checkChildren(child.children);
      }
      return false;
    });
  };

  return checkChildren(item.children);
};

const getBadgeCount = item => {
  if (!item.getterKeys?.count) return null;
  try {
    const count = useMapGetter(item.getterKeys.count).value;
    return count > 0 ? count : null;
  } catch (e) {
    return null;
  }
};

const shouldRenderComponent = item => {
  return typeof item.component === 'function' || isVNode(item.component);
};

const hasGrandChildren = item => {
  return (
    item.children && Array.isArray(item.children) && item.children.length > 0
  );
};

// Auto-expand items avec un enfant actif (un seul à la fois)
const updateExpandedItems = () => {
  // Fermer tous les items d'abord
  expandedItems.value.clear();
  // Ouvrir uniquement l'item qui contient la page active
  props.items.forEach(item => {
    if (hasActiveChild(item)) {
      expandedItems.value.add(item.name);
    }
  });
};

onMounted(() => {
  updateExpandedItems();
});

// Surveiller les changements de route
watch(
  () => route.path,
  () => {
    updateExpandedItems();
  }
);

// Gérer l'ouverture/fermeture d'un item (accordéon)
const handleToggleItem = (itemName, isOpen) => {
  if (isOpen) {
    // Fermer tous les autres items (comportement accordéon)
    expandedItems.value.clear();
    expandedItems.value.add(itemName);
  } else {
    expandedItems.value.delete(itemName);
  }
};
</script>

<template>
  <ul class="flex flex-col gap-2 m-0 list-none min-w-0">
    <template v-for="item in items" :key="item.name || item.section">
      <!-- Section header -->
      <SidebarMenuSection v-if="item.section" :title="item.section" />

      <!-- Item avec enfants (collapsible) -->
      <CollapsibleRoot
        v-else-if="item.children && item.children.length > 0"
        :open="expandedItems.has(item.name)"
        as-child
        @update:open="isOpen => handleToggleItem(item.name, isOpen)"
      >
        <SidebarMenuItem>
          <CollapsibleTrigger as-child>
            <SidebarMenuButton
              :is-active="isActive(item)"
              :has-active-child="hasActiveChild(item)"
              :is-expanded="expandedItems.has(item.name)"
              :tooltip="item.label"
              class="w-full min-w-0"
            >
              <Icon
                v-if="item.icon"
                :icon="item.icon"
                class="size-[18px] flex-shrink-0 transition-colors duration-200"
              />
              <span
                class="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap py-0.5"
              >
                {{ item.label }}
              </span>
              <Icon
                icon="i-lucide-chevron-down"
                class="size-[18px] flex-shrink-0 transition-transform duration-200"
                :class="{
                  'rotate-0': expandedItems.has(item.name),
                  '-rotate-90': !expandedItems.has(item.name),
                }"
              />
            </SidebarMenuButton>
          </CollapsibleTrigger>

          <!-- Sous-menu -->
          <CollapsibleContent
            class="overflow-hidden transition-all data-[state=closed]:animate-collapse-up data-[state=open]:animate-collapse-down min-w-0 max-w-full"
          >
            <SidebarMenuSub>
              <template v-for="subItem in item.children" :key="subItem.name">
                <!-- Sous-groupe imbriqué (avec ses propres enfants) -->
                <UNANavSubGroup
                  v-if="hasGrandChildren(subItem)"
                  :label="subItem.label"
                  :icon="subItem.icon"
                  :children="subItem.children"
                  :is-parent-expanded="expandedItems.has(item.name)"
                />

                <!-- Sous-item simple avec lien -->
                <SidebarMenuSubItem v-else-if="subItem.to">
                  <SidebarMenuSubButton :is-active="isActive(subItem)" as-child>
                    <router-link
                      :to="subItem.to"
                      :data-active="isActive(subItem)"
                      class="flex min-h-[36px] items-center gap-2.5 pl-3.5 pr-0 py-1.5 rounded-md w-full min-w-0 group will-change-auto transition-all duration-150 relative border border-transparent"
                      :class="{
                        'text-n-sidebar-text active bg-n-sidebar-subitem-active':
                          isActive(subItem),
                        'text-n-sidebar-text hover:bg-n-sidebar-item-hover':
                          !isActive(subItem),
                      }"
                      :style="
                        isActive(subItem)
                          ? { zIndex: 1, borderRadius: '0.375rem' }
                          : {}
                      "
                    >
                      <template v-if="shouldRenderComponent(subItem)">
                        <component
                          :is="
                            subItem.component({
                              label: subItem.label,
                              active: isActive(subItem),
                              icon: subItem.icon,
                            })
                          "
                        />
                      </template>
                      <template v-else>
                        <Icon
                          v-if="subItem.icon"
                          :icon="subItem.icon"
                          class="size-[18px] inline-block transition-colors duration-200 flex-shrink-0 text-n-sidebar-indicator"
                        />
                        <div
                          class="flex-1 truncate min-w-0 text-[15px] transition-all duration-200 py-0.5"
                        >
                          {{ subItem.label }}
                        </div>
                      </template>
                    </router-link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              </template>
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </CollapsibleRoot>

      <!-- Item simple (sans enfants) -->
      <SidebarMenuItem
        v-else-if="!item.section"
        class="relative"
        :class="{ 'mt-4': item.spacingTop, 'mb-0': item.spacingBottom }"
      >
        <SidebarMenuButton
          :is-active="isActive(item)"
          :tooltip="item.label"
          as-child
        >
          <router-link
            v-if="item.to"
            :to="item.to"
            class="flex items-center gap-2.5 w-full min-w-0"
          >
            <Icon
              v-if="item.icon"
              :icon="item.icon"
              class="size-[18px] flex-shrink-0 transition-colors duration-200"
            />
            <span
              class="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap py-0.5"
            >
              {{ item.label }}
            </span>
          </router-link>
        </SidebarMenuButton>

        <!-- Badge de notification -->
        <SidebarMenuBadge v-if="getBadgeCount(item)">
          {{ getBadgeCount(item) }}
        </SidebarMenuBadge>
      </SidebarMenuItem>
    </template>
  </ul>
</template>
